import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { useGetApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { useGetApiChannelDeployments } from "@/lib/api/generated/channels/channels";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";
import type {
  ChannelConnectionRecord,
  ChannelDeploymentRecord,
} from "../../../../../packages/shared-types/src/index.js";

export function ChannelDeploymentsPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: deployments, isLoading } = useGetApiChannelDeployments<ChannelDeploymentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: agents } = useGetApiAgents<ControlPlaneAgentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: connections } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  useDocumentTitle("Deployments");

  const agentName = (id: string) => agents?.find((a) => a.agentId === id)?.name ?? id;
  const connName = (id: string) => connections?.find((c) => c.id === id)?.displayName ?? id;

  if (isLoading) {return <TableSkeleton rows={3} cols={5} />;}

  if (!deployments || deployments.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span />
          <Button size="sm" onClick={() => navigate("/workspace/deployments/new")}>
            <Plus className="h-4 w-4 mr-1" /> New Deployment
          </Button>
        </div>
        <EmptyState title="No deployments" description="Deploy an agent to a channel connection." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>Channel Deployments</h2>
        <Button size="sm" onClick={() => navigate("/workspace/deployments/new")}>
          <Plus className="h-4 w-4 mr-1" /> New Deployment
        </Button>
      </div>
      <div className="border border-border rounded-[var(--radius-md)] overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: "var(--bg-accent)" }}>
              <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Agent</th>
              <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Connection</th>
              <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Status</th>
              <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>DMs</th>
              <th className="text-left px-4 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Groups</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map((dep) => (
              <tr key={dep.id} className="border-t border-border cursor-pointer hover:bg-[var(--bg-hover)] transition-colors" onClick={() => navigate(`/workspace/deployments/${dep.id}`)}>
                <td className="px-4 py-2.5" style={{ color: "var(--text-strong)" }}>{agentName(dep.agentId)}</td>
                <td className="px-4 py-2.5" style={{ color: "var(--muted-foreground)" }}>{connName(dep.channelConnectionId)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={dep.status} /></td>
                <td className="px-4 py-2.5" style={{ color: "var(--muted-foreground)" }}>{dep.allowDms ? "Yes" : "No"}</td>
                <td className="px-4 py-2.5" style={{ color: "var(--muted-foreground)" }}>{dep.allowGroups ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
